import { providers } from '../../providers';
import { wavespeed } from '../wavespeed';
import { baiduVod } from '../baidu-vod';
import { DEFAULT_VIDEO_UPSCALER_MODEL } from '../wavespeed/configs/upscaler';
import type { VideoModelEntry } from '../types';

// 价格表: 美元/秒
const VIDEO_TRANSLATE_PRICE_PER_SEC = { 'default': 0.0375 };
const VIDEO_WATERMARK_REMOVER_PRICE_PER_SEC = { 'default': 0.01 };
const VIDEO_FPS_BOOSTER_PRICE_PER_SEC = { 'default': 0.008 };
const VIDEO_DESUBTITLE_PRICE_PER_SEC = { 'default': 0.007 };
const UPSCALER_PRICE_PER_SEC: Record<string, Record<string, number>> = {
    'wavespeed-ai/video-upscaler': { '720p': 0.005, '1080p': 0.005, '2k': 0.01, '4k': 0.02 },
    'wavespeed-ai/video-upscaler-pro': { '720p': 0.02, '1080p': 0.03, '2k': 0.04, '4k': 0.05 },
    'wavespeed-ai/ultimate-video-upscaler': { '720p': 0.02, '1080p': 0.03, '2k': 0.05, '4k': 0.08 },
    'wavespeed-ai/seedvr2/video': { '720p': 0.02, '1080p': 0.03, '2k': 0.04, '4k': 0.05 },
    'wavespeed-ai/flashvsr': { '720p': 0.012, '1080p': 0.018, '2k': 0.024, '4k': 0.032 },
};
// 输入视频计费时长: 上游未带时长元数据时退回保守估计
function inputSeconds(providerOptions: any, minSeconds: number): number {
    return Math.max(minSeconds, Math.ceil(providerOptions?._media?.videoDurationSeconds ?? 15));
}
// 超分模型查找价格函数
function upscalerLookup(quality: string | undefined, providerOptions: any): number {
    const inputDur = inputSeconds(providerOptions, 5);
    const table = UPSCALER_PRICE_PER_SEC[(providerOptions?.upscaler_model as string | undefined) ?? DEFAULT_VIDEO_UPSCALER_MODEL] ?? UPSCALER_PRICE_PER_SEC[DEFAULT_VIDEO_UPSCALER_MODEL], rate = table[quality as string] ?? table['4k'];
    return inputDur * rate;
}
// 查找价格函数
const lookup = (table: Record<string, number>, quality?: string) =>
    table[quality?.toLowerCase() ?? ''] ?? Math.max(...Object.values(table));

const COST_FACTOR = 1.5;

export const miscModels: VideoModelEntry[] = [
    // 视频工具: 视频 AI 超分
    {
        id: 'video-upscaler',
        chef: providers['flowmuse'],
        hideInstructions: true,
        hideAspectRatio: true,
        hideDuration: true,
        tags: ['video-upscale'],
        providers: [
            {
                ...providers.flowmuse,
                model: wavespeed('video-upscaler'),
                getCost: ({ quality, providerOptions }) => {
                    return upscalerLookup(quality, providerOptions) * COST_FACTOR;
                },
                getPassCount: ({ quality, providerOptions }) => {
                    return upscalerLookup(quality, providerOptions) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            'v2v': {
                aspectRatios: [],
                durations: [],
                qualities: [],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '600s', formats: ['video/mp4'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    // 视频工具: 视频 AI 去水印
    {
        id: 'video-watermark-remover',
        chef: providers['flowmuse'],
        hideInstructions: true,
        hideAspectRatio: true,
        hideDuration: true,
        hideQuality: true,
        tags: ['video-watermark-remove'],
        providers: [
            {
                ...providers.flowmuse,
                model: wavespeed('wavespeed-ai/video-watermark-remover'),
                getCost: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_WATERMARK_REMOVER_PRICE_PER_SEC, 'default') * COST_FACTOR;
                },
                getPassCount: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_WATERMARK_REMOVER_PRICE_PER_SEC, 'default') * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            'v2v': {
                aspectRatios: [],
                durations: [],
                qualities: [],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '60s', formats: ['video/mp4'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    // 视频工具: 视频 AI 翻译
    {
        id: 'video-translate',
        chef: providers['flowmuse'],
        hideInstructions: true,
        hideAspectRatio: true,
        hideDuration: true,
        hideQuality: true,
        tags: ['video-translate'],
        providers: [
            {
                ...providers.flowmuse,
                model: wavespeed('heygen/video-translate'),
                getCost: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_TRANSLATE_PRICE_PER_SEC, 'default') * COST_FACTOR;
                },
                getPassCount: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_TRANSLATE_PRICE_PER_SEC, 'default') * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            'v2v': {
                aspectRatios: [],
                durations: [],
                qualities: [],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '600s', formats: ['video/mp4'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    // 视频工具: 视频字幕擦除（百度 VOD，AK/SK 鉴权通道）
    {
        id: 'video-desubtitle',
        chef: providers['flowmuse'],
        hideInstructions: true,
        hideAspectRatio: true,
        hideDuration: true,
        hideQuality: true,
        tags: ['video-desubtitle'],
        providers: [
            {
                ...providers.flowmuse,
                model: baiduVod('desubtitle'),
                getCost: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_DESUBTITLE_PRICE_PER_SEC, 'default') * COST_FACTOR;
                },
                getPassCount: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_DESUBTITLE_PRICE_PER_SEC, 'default') * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            'v2v': {
                aspectRatios: [],
                durations: [],
                qualities: [],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '600s', formats: ['video/mp4'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    // 视频工具: 视频 AI 补帧
    {
        id: 'video-fps-booster',
        chef: providers['flowmuse'],
        hideInstructions: true,
        hideAspectRatio: true,
        hideDuration: true,
        hideQuality: true,
        tags: ['video-fps-boost'],
        providers: [
            {
                ...providers.flowmuse,
                model: wavespeed('wavespeed-ai/video-fps-increaser'),
                getCost: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_FPS_BOOSTER_PRICE_PER_SEC, 'default') * COST_FACTOR;
                },
                getPassCount: ({ providerOptions }) => {
                    return inputSeconds(providerOptions, 1) * lookup(VIDEO_FPS_BOOSTER_PRICE_PER_SEC, 'default') * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            'v2v': {
                aspectRatios: [],
                durations: [],
                qualities: [],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '100MB', minDuration: '1s', maxDuration: '600s', formats: ['video/mp4'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];